import { getExerciseById } from './exercises';
import { INJURY_AVOID_MAP, type InjuryPart } from './injury-recovery-plan';

// All identities refer to the existing, reviewed static-stretch demonstrations.
const STRETCHES: Record<string, string[]> = {
  chest: ['library-personal-s1-chest-stretch'],
  back: ['library-personal-s1-rhomboid-stretch', 'library-personal-s1-back-tricep-stretch'],
  shoulders: ['library-personal-s1-posterior-delt-stretch', 'library-personal-s1-anterior-delt-stretch'],
  arms: ['library-personal-s1-bicep-stretch', 'library-personal-s1-back-tricep-stretch'],
  legs: ['library-personal-s1-glute-stretch', 'library-personal-s1-hamstring-stretch', 'library-personal-s1-quad-stretch'],
  calves: ['library-personal-s1-calf-stretch'],
  abs: ['library-personal-s1-abs-oblique-stretch'],
};
// Avoid stretches whose positioning uses a currently restricted joint or region.
const RESTRICTED_STRETCH_PARTS: Record<InjuryPart, string[]> = {
  shoulder: ['chest', 'back', 'shoulders', 'arms'],
  elbow: ['chest', 'back', 'shoulders', 'arms'],
  wrist: ['chest', 'back', 'shoulders', 'arms'],
  neck: ['chest', 'back', 'shoulders'],
  knee: ['legs', 'calves'],
  ankle: ['legs', 'calves'],
  hip: ['legs', 'calves', 'abs'],
  'lower-back': ['back', 'legs', 'abs'],
};

export function trainingCoolDownIds(mainIds: string[], existingIds: string[] = [], injuries: InjuryPart[] = []): string[] {
  const blocked = new Set(injuries.flatMap(part => RESTRICTED_STRETCH_PARTS[part] || []));
  const safe = (id: string) => {
    const ex = getExerciseById(id);
    if (!ex || !ex.isStretch) return false;
    const targetParts = Object.keys(STRETCHES).filter(part => STRETCHES[part].includes(ex.id));
    if (targetParts.some(part => blocked.has(part))) return false;
    return !injuries.some(part => {
      const rule = INJURY_AVOID_MAP[part];
      return rule?.subCategories?.includes(ex.subCategory) || rule?.keywords?.some(word => ex.name.includes(word));
    });
  };
  if (existingIds.length) return existingIds.filter(safe);
  const parts = [...new Set(mainIds.flatMap(id => { const part = getExerciseById(id)?.bodyPart; return part ? [part] : []; }))];
  const armCategories = new Set(mainIds.map(id => getExerciseById(id)).filter(ex => ex?.bodyPart === 'arms').map(ex => ex?.subCategory));
  const armIds = [
    ...(armCategories.has('triceps') ? ['library-personal-s1-back-tricep-stretch'] : []),
    ...(armCategories.has('biceps') ? ['library-personal-s1-bicep-stretch'] : []),
  ];
  const groups = parts.filter(part => !blocked.has(part)).map(part =>
    (part === 'arms' && armIds.length ? armIds : STRETCHES[part] || []).filter(safe));
  // Cover each trained region first, then complement it, with a bounded cool-down.
  return [...new Set([...groups.map(ids => ids[0]).filter((id): id is string => !!id), ...groups.flat()])].slice(0, 3);
}
